import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { guides, guideCategories } from "@/mocks/guides";
import type { Guide } from "@/types/content";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import GuideCard from "@/components/feature/GuideCard";

const SITE = "https://son4min.cn";

export default function Guides() {
  const allGuides = guides as Guide[];
  const [activeCategory, setActiveCategory] = useState("all");

  const featured = allGuides[0];

  const list = useMemo(() => {
    const rest = allGuides.slice(1);
    if (activeCategory === "all") return rest;
    return rest.filter((guide) => guide.categoryKey === activeCategory);
  }, [activeCategory, allGuides]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "维权指南：防骗知识与投诉流程",
      url: `${SITE}/guides`,
      inLanguage: "zh-CN",
      description: "整理防骗知识、维权流程与消费提醒，帮助用户在遭遇跑路、诈骗或消费纠纷时知道该怎么做。",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: allGuides.length,
        itemListElement: allGuides.map((guide, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE}/guides/${guide.id}`,
          name: guide.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "维权指南", item: `${SITE}/guides` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title="维权指南_防骗知识与投诉流程 - 避雷哨"
        description="避雷哨维权指南整理防骗知识、投诉流程与消费提醒，包括证据固定、资金盘识别、退款维权与举报渠道清单，帮你知道该找谁、怎么说。"
        keywords="维权案例,防骗知识,投诉流程,举报渠道,消费提醒"
        path="/guides"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100 to-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "维权指南" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            维权指南：出问题以后该怎么做
          </h1>
          <p className="mt-3 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            把常见场景拆成可以直接照做的步骤：先固定证据，再逐级反映，最后留存跟进记录。遇到同类情况时，照流程走能省下不少时间。
          </p>
        </div>
      </section>

      {featured && (
        <section className="mx-auto max-w-content px-4 pt-10 md:px-6 md:pt-12">
          <Link
            to={`/guides/${featured.id}`}
            className="group grid grid-cols-1 overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-secondary-300 cursor-pointer lg:grid-cols-[1.1fr_1fr]"
          >
            <div className="h-52 w-full overflow-hidden bg-background-100 lg:h-full">
              <img
                src={featured.cover}
                alt={`${featured.title} 配图`}
                title={`${featured.title} - 维权指南`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-9">
              <div className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
                <span className="rounded-full bg-accent-50 px-2.5 py-1 text-accent-700">重点推荐</span>
                <span className="rounded-full bg-secondary-50 px-2.5 py-1 text-secondary-800">{featured.category}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {featured.readMinutes} 分钟读完
                </span>
              </div>
              <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-2xl">
                {featured.title}
              </h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                {featured.summary}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 font-label text-sm font-medium text-primary-700">
                阅读全文
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          {guideCategories.map((item) => {
            const active = activeCategory === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveCategory(item.key)}
                className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {item.name}
              </button>
            );
          })}
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {list.map((guide) => (
              <GuideCard key={guide.id} guide={guide} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-book-open-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">这个分类下还没有指南</h2>
            <p className="mt-2 max-w-md font-body text-sm text-foreground-600">
              我们正在持续补充内容，可以先看看其他分类，或者浏览避雷名单。
            </p>
            <button
              type="button"
              onClick={() => setActiveCategory("all")}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              查看全部指南
            </button>
          </div>
        )}
      </section>
    </>
  );
}